/**
 * Shared helpers for AASB rulepacks. Keeps the per-standard files focused on rules.
 */
import { Finding } from './types';

export type CellValue = string | number | boolean | null;

export function ulid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

export function fmt(n: number): string {
  return n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function parseNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).replace(/[$,£€% ]/g, '').trim();
  const neg = /^\(.*\)$/.test(s);
  const clean = neg ? s.slice(1, -1) : s;
  const n = Number(clean);
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}

export function parsePercent(v: unknown): number | null {
  const n = parseNumber(v);
  if (n == null) return null;
  return n > 1 ? n / 100 : n;
}

export function parseBool(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (v == null || v === '') return false;
  const s = String(v).toLowerCase().trim();
  return s === 'true' || s === 'yes' || s === 'y' || s === '1' || s === 'met' || s === 'complete';
}

export function parseDate(v: unknown): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return isNaN(+v) ? null : v;
  if (typeof v === 'number') {
    const ms = (v - 25569) * 86400 * 1000;
    const d = new Date(ms);
    return isNaN(+d) ? null : d;
  }
  const s = String(v).trim();
  const au = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (au) {
    const [, dd, mm, yyyy] = au;
    const year = yyyy.length === 2 ? 2000 + Number(yyyy) : Number(yyyy);
    const d = new Date(year, Number(mm) - 1, Number(dd));
    return isNaN(+d) ? null : d;
  }
  const iso = new Date(s);
  return isNaN(+iso) ? null : iso;
}

export function monthsBetween(a: Date, b: Date): number {
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) +
    (b.getDate() >= a.getDate() ? 0 : -1);
}

export function makeHeaderMap(specs: Array<[string, ...string[]]>): Record<string, string> {
  const m: Record<string, string> = {};
  for (const [canonical, ...syns] of specs) {
    m[canonical.toLowerCase()] = canonical;
    for (const s of syns) m[s.toLowerCase()] = canonical;
  }
  return m;
}

export function normaliseHeader(map: Record<string, string>, h: string): string | null {
  return map[String(h ?? '').trim().toLowerCase()] ?? null;
}

export function acceptedHeadersFor(map: Record<string, string>, canonical: string): string[] {
  return Object.entries(map).filter(([, c]) => c === canonical).map(([k]) => k);
}

export interface FindingBuilder {
  build(
    ruleId: string,
    severity: Finding['severity'],
    title: string,
    message: string,
    opts?: Partial<Pick<Finding, 'rowId' | 'evidence' | 'suggestedAdjustment'>>
  ): Finding;
}

export function findingBuilder(standard: Finding['standard'], rulepackVersion: string): FindingBuilder {
  return {
    build(ruleId, severity, title, message, opts = {}) {
      return {
        id: ulid(),
        ruleId,
        standard,
        severity,
        title,
        message,
        evidence: opts.evidence ?? {},
        suggestedAdjustment: opts.suggestedAdjustment,
        rowId: opts.rowId,
        rulepackVersion,
      };
    },
  };
}

export function severityCounts(findings: Finding[]): { info: number; warning: number; error: number } {
  return {
    info: findings.filter(f => f.severity === 'info').length,
    warning: findings.filter(f => f.severity === 'warning').length,
    error: findings.filter(f => f.severity === 'error').length,
  };
}

export function compliantCount(rowIds: string[], findings: Finding[]): number {
  const dirty = new Set<string>();
  for (const f of findings) {
    if ((f.severity === 'error' || f.severity === 'warning') && f.rowId) dirty.add(f.rowId);
  }
  return rowIds.filter(id => !dirty.has(id)).length;
}
